import React, { useEffect, useState } from 'react';
import { FormControl, Select, MenuItem, TextField, Box, Typography } from '@mui/material';
import API from '@Services/apiAxios';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';

type Address = {
  line1: string;
  line2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
};

type Warehouse = {
  _id: string;
  name: string;
  _addressId: Address;
};

type Props = {
  onSelect?: (
    pickup: {
      city: string;
      state: string;
      country: string;
      pincode: string;
    },
    warehousePincode: string,
  ) => void;
};

const WarehouseSelector: React.FC<Props> = ({ onSelect }) => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const countryHashById = useSelector((state: RootStateType) => state.global.countriesObj);

  useEffect(() => {
    API.get('/shipment/warehouse/all')
      .then(res => setWarehouses(res.data.data.list))
      .catch(err => console.error('API Error:', err));
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (event: any) => {
    const id = event.target.value;
    setSelectedId(id);

    const warehouse = warehouses.find(w => w._id === id) || null;
    setSelectedWarehouse(warehouse);

    if (warehouse && onSelect) {
      const { city, state, pincode, country: countryId } = warehouse._addressId;
      const country = countryHashById?.[countryId]?.name;
      onSelect({ city, state, country, pincode }, pincode);
    }
  };

  return (
    <Box width={'85%'}>
      <Typography fontWeight={500} marginBottom={'8px'} fontSize={'14px'}>
        Drop-off Warehouse *
      </Typography>
      <Box display="flex" gap={2}>
        <FormControl fullWidth>
          <Select
            value={selectedId}
            onChange={handleChange}
            displayEmpty
            renderValue={selected => {
              if (!selected) {
                return <span style={{ color: '#888' }}>Select Warehouse</span>;
              }
              const wh = warehouses.find(w => w._id === selected);
              return `${wh?.name} - ${wh?._addressId.line1}, ${wh?._addressId.city}`;
            }}
          >
            <MenuItem disabled value="">
              Select Warehouse
            </MenuItem>
            {warehouses?.map(w => (
              <MenuItem key={w._id} value={w._id}>
                {w.name} - {w._addressId.line1}, {w._addressId.city}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          placeholder="Pincode"
          value={selectedWarehouse?._addressId.pincode || ''}
          disabled
          fullWidth
          variant="outlined"
          InputLabelProps={{ shrink: false }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-disabled': {
                cursor: 'not-allowed',
                '& fieldset': {
                  borderColor: '#ccc',
                },
                '& input': {
                  cursor: 'not-allowed',
                },
              },
            },
          }}
        />
      </Box>

      {selectedWarehouse && (
        <Typography>
          {selectedWarehouse?._addressId?.state}, {selectedWarehouse?._addressId?.city},
          {countryHashById?.[selectedWarehouse?._addressId?.country]?.name}, {selectedWarehouse?._addressId?.pincode}
        </Typography>
      )}
    </Box>
  );
};

export default WarehouseSelector;
